import { memo, useEffect, useState } from "react";
import type { BotezBokehComponentProps } from "./types";
import {
  CORAL,
  CORAL_TEXT,
  CORAL_TEXT_LARGE,
  TEAL_DARK,
  FONT_DISPLAY,
  FONT_KICKER,
  FONT_SCRIPT,
  TEAL,
  calculateCountdown,
  usePrefersReducedMotion,
} from "./utils";
import { countdownUnitLabelKey } from "../shared/countdown";

export const BotezBokehCountdown = memo(({ event, config, t }: BotezBokehComponentProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [timeLeft, setTimeLeft] = useState(() => calculateCountdown(event.date));

  useEffect(() => {
    // Under prefers-reduced-motion, tick once per minute instead of every second
    // so the page doesn't constantly animate (rule 11).
    const timer = setInterval(
      () => setTimeLeft(calculateCountdown(event.date)),
      prefersReducedMotion ? 60000 : 1000,
    );
    return () => clearInterval(timer);
  }, [event.date, prefersReducedMotion]);

  if (!event.date) return null;

  // JUB-94: singular/plural label per unit ("1 zi" vs "N zile"), the same
  // rule the golden_serenity template uses -- see ../shared/countdown.
  const units = [
    { value: timeLeft.days, label: t(countdownUnitLabelKey("days", timeLeft.days)) },
    { value: timeLeft.hours, label: t(countdownUnitLabelKey("hours", timeLeft.hours)) },
    { value: timeLeft.minutes, label: t(countdownUnitLabelKey("minutes", timeLeft.minutes)) },
    { value: timeLeft.seconds, label: t(countdownUnitLabelKey("seconds", timeLeft.seconds)) },
  ];

  return (
    <section className="relative z-10 py-16" style={{ background: config.palette.background }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center">
          <h2
            className="text-4xl sm:text-5xl mb-8"
            style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
          >
            {t("events.form.template.countdownTitle")}
          </h2>

          <div className="flex justify-center items-center gap-3 px-4 sm:px-0 sm:gap-6 md:gap-8 mb-4">
            {units.map((item, index) => (
              <div key={item.label} className="flex flex-col items-center relative">
                {/* Playful blob behind each number */}
                <div
                  className="absolute inset-0 -m-3 opacity-15 -z-10"
                  aria-hidden="true"
                  style={{
                    background: index % 2 === 0 ? CORAL : TEAL,
                    borderRadius: "50% 40% 60% 40% / 60% 50% 40% 50%",
                    transform: `rotate(${index * 15}deg)`,
                  }}
                />
                <div
                  className="text-3xl sm:text-5xl md:text-6xl leading-none mb-2 font-bold px-1 sm:px-3 py-2 min-w-[4.5rem] sm:min-w-0 text-center"
                  style={{ fontFamily: FONT_DISPLAY, color: TEAL }}
                >
                  {String(item.value).padStart(2, "0")}
                </div>
                <div
                  className="text-[10px] md:text-xs tracking-[0.15em] uppercase font-medium"
                  style={{ fontFamily: FONT_KICKER, color: CORAL_TEXT }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          <p
            className="text-xl sm:text-2xl mt-8"
            style={{ fontFamily: FONT_SCRIPT, color: TEAL_DARK }}
          >
            {t("events.form.untilBigDay")}
          </p>
        </div>
      </div>
    </section>
  );
});

BotezBokehCountdown.displayName = "BotezBokehCountdown";
